import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import ReportsArchiveTableRowComponent from 'webapp/components/reports/archive/table-row';
import TableSorterComponent from 'webapp/components/table-sorter';

import type { AddTagAction, DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortReportDelivery from 'webapp/models-custom/filter-sort/report-delivery';
import type Report from 'webapp/models-custom/report';
import type ReportDelivery from 'webapp/models-custom/report-delivery';

type Args = Record<string, never>;

type Action = AddTagAction | DeleteAction;

export default class ReportsArchiveTableComponent extends Component<Args> {
	@service('model-manager/report-deliveries')
	declare mmReportDeliveriesService: Service.ModelManager.ReportDeliveries;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	filterSort: FilterSortReportDelivery;
	batchSelect: BatchSelect<ReportDelivery>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortReportDelivery({
			getAllItems: () => {
				return this.model;
			},
		});

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as Action);
			},
		});
	}

	private _model: ReportDelivery[] = [];

	@cached
	get model(): ReportDelivery[] {
		this.mmReportDeliveriesService.updateModelCache(
			this._model,
			this.mmReportDeliveriesService.savedRecords,
		);
		return this._model;
	}

	get batchButtonContainerNode() {
		return isAlive(this) && this.selectedItems.length
			? this.navigationService.batchButtonContainerNode
			: null;
	}

	get selectedItems(): ReportDelivery[] {
		return this.batchSelect.selectedItems;
	}

	get batchOptions(): PopupListItem<Action>[] {
		const batchSelect = this.batchSelect;
		return [batchSelect.addTagsOption, batchSelect.deleteOption];
	}

	doBatchAction(actionName: Action) {
		return this[actionName]();
	}

	@action
	batchDelete() {
		return this.mmReportDeliveriesService.batchDelete(this.selectedItems);
	}

	@action
	batchUpdateTags() {
		const reports: Report[] = [];

		this.selectedItems.forEach((delivery) => {
			const report = delivery.report;
			if (report && !reports.find((existingReport) => existingReport.id === report.id)) {
				reports.push(report);
			}
		});

		return this.mmReportsService.batchUpdateTags(reports);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		<FilterNoResultsComponent @model={{this.filterSort}} />

		<div id="reports-archive-list" class="bottom-padding">

			<table
				class="flexible-table bigtable has-checkbox has-avatar
					{{if this.filterSort.noResults 'hide'}}"
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange this.filterSort.results}}
			>
				<colgroup>
					<col class="cc" />
					<col class="h0" />
					<col width="20%" />
					<col />
					<col />
					<col class="button-ct" />
				</colgroup>
				<thead>
					<tr>
						<CommonBatchTableHeaderComponent @batchSelect={{this.batchSelect}} />
						<th class="h0">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="title"
								@name="Name"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th></th>
						<th class="prio-low">Recipient(s)</th>
						<th class="prio-high">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="timestamp"
								@name="Generated"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-low">Tags</th>
						{{! <th class="prio-low">Approved by</th> }}
						<th class="button-ct"></th>
					</tr>
				</thead>
				<tbody>
					{{#each this.filterSort.results as |result|}}
						<ReportsArchiveTableRowComponent
							@model={{result}}
							@batchSelect={{this.batchSelect}}
						/>
					{{/each}}
				</tbody>
			</table>

		</div>

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
